import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { MailWarning, Loader2, AlertTriangle, RotateCcw, CheckCircle2, Wand2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';

// Surfaces leads whose stored email is malformed (e.g. "name@gmailcom") — these
// block the edit form from saving. Confidently-fixable typos are auto-corrected;
// unfixable junk (placeholders like "INFO@XYZ") can be cleared so the lead saves.
// All driven by additive /api/leads/fix-emails endpoints (preview → apply → undo).
export default function EmailCleanupBadge() {
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [fixableCount, setFixableCount] = useState(0);
  const [unfixableCount, setUnfixableCount] = useState(0);
  const [totalMatching, setTotalMatching] = useState(0);
  const [message, setMessage] = useState('');
  const [applying, setApplying] = useState(false);
  const [lastBatchId, setLastBatchId] = useState(null);
  const [undoing, setUndoing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/api/leads/backfill-status');
      setCount(data?.malformed_emails || 0);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);

  const openPreview = async () => {
    setOpen(true);
    setLoading(true);
    setCandidates([]);
    setLastBatchId(null);
    try {
      const { data } = await api.get('/api/leads/fix-emails/preview');
      const cands = (data?.candidates || []).slice().sort((a, b) => (b.fixable === true) - (a.fixable === true));
      setCandidates(cands);
      setFixableCount(data?.fixable_count || 0);
      setUnfixableCount(data?.unfixable_count || 0);
      setTotalMatching(data?.total_matching || cands.length);
      setMessage(data?.message || '');
      // Pre-select only the confidently-fixable typos. Clearing junk is opt-in.
      setSelected(new Set(cands.filter((c) => c.fixable).map((c) => c.id)));
    } catch (e) {
      toast.error('Could not load preview. Try again.');
      setOpen(false);
    } finally { setLoading(false); }
  };

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const fixableIds = candidates.filter((c) => c.fixable).map((c) => c.id);
  const selectFixableOnly = () => setSelected(new Set(fixableIds));
  const selectAll = () => setSelected(new Set(candidates.map((c) => c.id)));
  const clearSelection = () => setSelected(new Set());

  const apply = async () => {
    const ids = Array.from(selected);
    if (!ids.length) { toast.info('Select at least one email to fix.'); return; }
    setApplying(true);
    try {
      const { data } = await api.post('/api/leads/fix-emails/apply', { lead_ids: ids });
      toast.success(data?.message || 'Emails updated.');
      setLastBatchId(data?.batch_id || null);
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not apply fixes. Try again.');
    } finally { setApplying(false); }
  };

  const undo = async () => {
    if (!lastBatchId) return;
    setUndoing(true);
    try {
      const { data } = await api.post('/api/leads/fix-emails/undo', { batch_id: lastBatchId });
      toast.success(data?.message || 'Reverted.');
      setLastBatchId(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not undo. Try again.');
    } finally { setUndoing(false); }
  };

  if (count <= 0 && !lastBatchId) return null;

  const selFixable = candidates.filter((c) => c.fixable && selected.has(c.id)).length;
  const selClear = candidates.filter((c) => !c.fixable && selected.has(c.id)).length;

  return (
    <div className="inline-flex items-center gap-2">
      {count > 0 && (
        <Button
          size="sm" variant="outline"
          className="h-7 border-rose-400 text-rose-700 hover:bg-rose-100 dark:text-rose-300 dark:hover:bg-rose-900/40 text-xs"
          onClick={openPreview}
          data-testid="fix-emails-btn"
          title="Review and fix leads saved with a malformed email so they can be edited and emailed"
        >
          <MailWarning className="h-3.5 w-3.5 mr-1.5" />
          Fix {count} email{count === 1 ? '' : 's'}
        </Button>
      )}
      {lastBatchId && (
        <Button
          size="sm" variant="ghost"
          className="h-7 text-xs text-muted-foreground hover:text-foreground"
          onClick={undo}
          disabled={undoing}
          data-testid="undo-email-fix-btn"
          title="Revert the last email-fix batch"
        >
          {undoing ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
          Undo last fix
        </Button>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) load(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="fix-emails-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MailWarning className="h-4 w-4 text-rose-500" /> Fix malformed emails
            </DialogTitle>
            <DialogDescription>
              Confident typo fixes (e.g. <span className="font-mono">name@gmailcom → name@gmail.com</span>) are pre-selected.
              <span className="text-amber-600 font-medium"> Unfixable junk</span> (placeholders like <span className="font-mono">INFO@XYZ</span>) can be
              selected to <span className="font-medium">clear</span> the invalid email so the lead can be saved. You can undo the whole batch.
            </DialogDescription>
          </DialogHeader>

          {!loading && candidates.length > 0 && (
            <div className="flex items-center justify-between gap-2 px-1 pb-1 shrink-0" data-testid="fix-emails-toolbar">
              <span className="text-sm font-medium">{candidates.length} shown{totalMatching > candidates.length ? ` of ${totalMatching}` : ''}</span>
              <div className="flex items-center gap-1.5">
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={selectFixableOnly} data-testid="fix-emails-select-fixable" title="Select only the confident auto-corrections">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Fixable only ({fixableIds.length})
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={selectAll} data-testid="fix-emails-select-all">
                  Select all
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={clearSelection} data-testid="fix-emails-clear-selection">
                  Clear
                </Button>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-auto min-h-0 rounded-md border divide-y">
            {loading && (
              <div className="flex items-center gap-2 p-4 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading preview…
              </div>
            )}
            {!loading && candidates.length === 0 && (
              <div className="p-4 text-sm text-muted-foreground" data-testid="fix-emails-empty">
                {message || 'Nothing to fix — every email is valid.'}
              </div>
            )}
            {!loading && candidates.map((c) => {
              const checked = selected.has(c.id);
              return (
                <label
                  key={c.id}
                  className="flex items-center gap-3 p-2.5 text-sm cursor-pointer hover:bg-muted/40"
                  data-testid={`fix-email-row-${c.id}`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(c.id)}
                    className="h-4 w-4 shrink-0 accent-primary"
                    data-testid={`fix-email-check-${c.id}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{c.name}</div>
                    <div className="text-xs text-muted-foreground font-mono break-all">
                      {c.current_email} <span className="mx-1">→</span>
                      {c.fixable ? (
                        <span className="text-foreground font-medium">{c.proposed_email}</span>
                      ) : (
                        <span className="text-rose-500 font-medium">(clear)</span>
                      )}
                    </div>
                  </div>
                  {c.fixable ? (
                    <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-medium text-emerald-600 bg-emerald-500/10 border border-emerald-500/30 rounded px-1.5 py-0.5" data-testid={`fix-email-fixable-${c.id}`}>
                      <Wand2 className="h-3 w-3" /> Auto-fix
                    </span>
                  ) : (
                    <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-medium text-amber-600 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5" data-testid={`fix-email-manual-${c.id}`}>
                      <AlertTriangle className="h-3 w-3" /> Manual
                    </span>
                  )}
                </label>
              );
            })}
          </div>

          <DialogFooter className="shrink-0 flex items-center justify-between sm:justify-between gap-2">
            <span className="text-xs text-muted-foreground mr-auto" data-testid="fix-emails-selected-count">
              {selFixable} to correct{selClear > 0 ? `, ${selClear} to clear` : ''}
            </span>
            <Button variant="outline" size="sm" onClick={() => { setOpen(false); load(); }} data-testid="fix-emails-cancel-btn">Cancel</Button>
            <Button size="sm" onClick={apply} disabled={applying || selected.size === 0} data-testid="fix-emails-apply-btn">
              {applying ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
              Apply {selected.size} fix{selected.size === 1 ? '' : 'es'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
